import { Box, Button, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useFieldArray,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	Fragment,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { YESNO, YESNOLIST } from '@/common/constants/common';
import { useGetDicListQuery } from '@/api/common';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import IconButton from '@mui/material/IconButton';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditFamilyCarersInformationForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();

		const { data: relationshipDicList } = useGetDicListQuery('relationship', {
			refetchOnMountOrArgChange: true,
		});

		const defaultValues: DefaultValues<any> = {};

		useEffect(() => {
			if (details) {
				if (details.familyCarersList) {
					const arr = details.familyCarersList.map((item: any) => {
						return {
							...item,
							emergencyContact: item.emergencyContact ? true : false,
						};
					});
					methods.setValue('familyCarersList', arr);
				}
			}
		}, [details]);

		const formSchema = Yup.object().shape({
			familyCarersList: Yup.array().of(
				Yup.object().shape({
					familyCarerName: Yup.string().required(t('errorMessage.pleaseEnter')),
					age: Yup.string().nullable(),
					address: Yup.string().required(t('errorMessage.pleaseEnter')),
					telNo: Yup.string()
						.required(t('errorMessage.pleaseEnter'))
						.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
					relationship: Yup.string().required(t('errorMessage.pleaseSelect')),
					emergencyContact: Yup.string().required(
						t('errorMessage.pleaseSelect')
					),
				})
			),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const {
			fields: familyCarersFields,
			append: familyCarersAppend,
			replace: familyCarersReplace,
			update: familyCarersUpdate,
		}: any = useFieldArray({
			control: methods.control,
			name: 'familyCarersList',
		});

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		const addFamilyCarers = () => {
			familyCarersAppend({});
		};

		// Removing a row shifts the field name of every row below it. The inputs are
		// controllers bound to those names, so with remove() they keep the value they
		// held under the old index and the last row's values drop out of the form
		// state - the row still shows them, but submitting reports it as empty.
		// Rebuilding the list with replace() hands every row a fresh key, so they
		// remount against the shifted values.
		const removeFamilyCarers = (index: number) => {
			familyCarersReplace(
				(methods.getValues('familyCarersList') || []).filter(
					(_: any, i: number) => i !== index
				)
			);
		};

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.familyCarersInformation')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e) => e.preventDefault())}>
						{familyCarersFields.map((item: any, index: number) => (
							<Fragment key={item.id}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
									}}
									marginBottom={1}
								>
									<Typography variant='body1'>
										{t('preAdmission.familyCarer') + ` ${index + 1}`}
									</Typography>
									<IconButton
										onClick={() => {
											removeFamilyCarers(index);
										}}
									>
										<CloseOutlinedIcon />
									</IconButton>
								</Box>

								<Grid container spacing={3} marginTop={0} marginBottom={2}>
									<Grid size={6}>
										<AppTextField
											control={methods.control}
											name={`familyCarersList[${index}].familyCarerName`}
											label={t('preAdmission.name') + '*'}
										/>
									</Grid>
									<Grid size={6}>
										<AppTextField
											control={methods.control}
											name={`familyCarersList[${index}].age`}
											label={t('preAdmission.age')}
										/>
									</Grid>
									<Grid size={6}>
										<AppAutoCompleteMenu
											control={methods.control}
											name={`familyCarersList[${index}].relationship`}
											label={t('preAdmission.relationship') + '*'}
											options={(relationshipDicList?.data || []).map(
												(item: { labelEn: any }) => {
													return {
														...item,
														name: item.labelEn,
													};
												}
											)}
										/>
									</Grid>
									<Grid size={6}>
										<AppTextField
											control={methods.control}
											name={`familyCarersList[${index}].address`}
											label={t('preAdmission.address') + '*'}
										/>
									</Grid>
									<Grid size={6}>
										<AppTextField
											control={methods.control}
											name={`familyCarersList[${index}].telNo`}
											label={t('preAdmission.telNo') + '*'}
										/>
									</Grid>
									<Grid size={6}>
										<Typography variant='body2' color='common.neutral40'>
											{t('preAdmission.emergencyContact') + '*'}
										</Typography>
										<AppRadioGroup
											control={methods.control}
											name={`familyCarersList[${index}].emergencyContact`}
											row
											options={YESNO}
										/>
									</Grid>
								</Grid>
							</Fragment>
						))}
						{familyCarersFields.length < 30 && (
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={12}>
									<Button
										startIcon={<AddCircleOutlineOutlinedIcon />}
										size='large'
										onClick={addFamilyCarers}
									>
										{t('button.newFamilyCarer')}
									</Button>
								</Grid>
							</Grid>
						)}
					</form>
				</FormProvider>
			</>
		);
	}
);

EditFamilyCarersInformationForm.displayName = 'EditFamilyCarersInformationForm';

export default EditFamilyCarersInformationForm;
